import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useIntl } from "react-intl";
import { RegistrationStatus } from "services/KnowfullyAdminClient";
import { dateToStandardFormat } from "utils/dateUtils";
import { pairKey, ReviewRow } from "./types";

type ReviewTableProps = {
  rows: ReviewRow[];
  statusByPair: Record<string, RegistrationStatus>;
  isFetching: boolean;
  newCount: number;
  extendCount: number;
};

const ReviewTable = ({ rows, statusByPair, isFetching, newCount, extendCount }: ReviewTableProps) => {
  const { formatMessage: t } = useIntl();

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Typography variant="h6">
          {t({ id: "bulkRegistrationReview", defaultMessage: "Review Registrations" })}
        </Typography>
        <Chip
          size="small"
          label={t({ id: "bulkRegistrationRowCount", defaultMessage: "{count} rows" }, { count: rows.length })}
        />
        {isFetching && <CircularProgress size={16} />}
      </Box>
      {rows.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          {t({
            id: "bulkRegistrationEmpty",
            defaultMessage: "Add at least one user and one SKU above to see combinations.",
          })}
        </Typography>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t({ id: "user", defaultMessage: "User" })}</TableCell>
                  <TableCell>{t({ id: "skuLabel", defaultMessage: "SKU" })}</TableCell>
                  <TableCell>
                    {t({ id: "bulkRegistrationCurrentStatus", defaultMessage: "Current Status" })}
                  </TableCell>
                  <TableCell>
                    {t({ id: "bulkRegistrationCurrentEndDate", defaultMessage: "Current End Date" })}
                  </TableCell>
                  <TableCell>{t({ id: "bulkRegistrationAction", defaultMessage: "Action" })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const status = statusByPair[pairKey(row.userId, row.skuId)];
                  const isExisting = status?.isExisting;
                  let statusText = "…";
                  if (status) {
                    statusText = isExisting
                      ? t({ id: "bulkRegistrationActive", defaultMessage: "Active" })
                      : t({ id: "bulkRegistrationNotRegistered", defaultMessage: "Not registered" });
                  }
                  return (
                    <TableRow key={pairKey(row.userId, row.skuId)}>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>
                        {row.code}
                        {row.title ? ` — ${row.title}` : ""}
                      </TableCell>
                      <TableCell>{statusText}</TableCell>
                      <TableCell>
                        {status?.currentEndDate ? dateToStandardFormat(status.currentEndDate) : "—"}
                      </TableCell>
                      <TableCell>
                        {status && (
                          <Chip
                            size="small"
                            color={isExisting ? "primary" : "success"}
                            label={
                              isExisting
                                ? t({ id: "bulkRegistrationExtend", defaultMessage: "Extend" })
                                : t({ id: "bulkRegistrationNew", defaultMessage: "New registration" })
                            }
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box display="flex" gap={2} mt={2}>
            {newCount > 0 && (
              <Typography variant="body2">
                ● {t({ id: "bulkRegistrationNewSummary", defaultMessage: "{count} new" }, { count: newCount })}
              </Typography>
            )}
            {extendCount > 0 && (
              <Typography variant="body2">
                ● {t({ id: "bulkRegistrationExtendSummary", defaultMessage: "{count} extend" }, { count: extendCount })}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default ReviewTable;
